import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock, MapPin } from "lucide-react";
import { fetchPackages } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { IMAGES } from "@/config/siteConfig";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FinalCTA } from "@/components/home/SocialSections";

const ItinerariesPage = () => {
  useSEO({
    title: "Kashmir Itineraries | 3 to 7 Day Kashmir Trip Plans | Book Kashmir",
    description: "Sample Kashmir itineraries from 3 to 7 days covering Srinagar, Gulmarg, Pahalgam and Sonamarg. Every itinerary is personalised to your dates and budget.",
    path: "/kashmir-itineraries",
  });
  const { data: packages = [], isLoading } = useQuery({ queryKey: ["packages"], queryFn: fetchPackages });

  return (
    <main data-testid="itineraries-page">
      <PageHero image={IMAGES.gurez} overline="Plan Your Days" title="Kashmir Itineraries"
        subtitle="Sample day-by-day Kashmir trip plans — use them as a starting point and we'll personalise every detail around you." />
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Sample Plans" title="Day-by-Day Kashmir Trip Ideas"
            subtitle="From a quick 4-day escape to a full week across four valleys — pick a plan and we'll tailor it to you." />
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">{[1, 2].map((i) => <Skeleton key={i} className="h-72 rounded-xl" />)}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {packages.map((pkg) => (
                <article key={pkg.slug} className="overflow-hidden rounded-xl bg-white ring-1 ring-[#0F2F24]/8 shadow-sm" data-testid={`itinerary-card-${pkg.slug}`}>
                  <div className="flex flex-col sm:flex-row">
                    <img src={pkg.image} alt={`${pkg.name} itinerary`} loading="lazy" className="h-44 w-full sm:h-auto sm:w-44 object-cover" />
                    <div className="flex-1 p-5">
                      <h3 className="font-serif text-lg font-semibold text-[#0F2F24]">{pkg.name}</h3>
                      <p className="mt-1 flex items-center gap-1.5 text-xs font-medium text-[#C28519]">
                        <Clock className="h-3.5 w-3.5" /> {pkg.duration}
                      </p>
                      <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                        <MapPin className="h-3.5 w-3.5" /> {pkg.route.join(" → ")}
                      </p>
                      <Button asChild size="sm" className="mt-4 bg-[#0F2F24] text-white hover:bg-[#1a4232]">
                        <Link to={`/packages/${pkg.slug}`} data-testid={`itinerary-view-${pkg.slug}`}>
                          View Full Itinerary <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
      <FinalCTA />
    </main>
  );
};

export default ItinerariesPage;
